import {
  Tokens,
  AuthAdapter,
  SignInWithPasswordResult,
  SignInWithPasswordSuccess,
} from '../../types';

// eslint-disable-next-line import/no-unassigned-import
import 'url-search-params-polyfill';

type ApigeeAuthAdapterOptions = {
  /**
   * Accepts a resolver so the gateway is read per request. The
   * `useLegacyApigeeEndpoint` flag can move traffic between api-edge and Apigee
   * at runtime, and a token must be minted at whichever gateway the GraphQL
   * requests will be sent to — resolving this once at module load pinned the
   * token host while GraphQL moved, and the mismatched token was rejected.
   */
  baseUrl: string | (() => string);
  clientId: string;
};

/**
 * Handles custom Apigee Token endpoints
 *
 */
class ApigeeAuthAdapter implements AuthAdapter {
  // eslint-disable-next-line no-empty-function
  constructor(private options: ApigeeAuthAdapterOptions) {}

  private get baseUrl(): string {
    const { baseUrl } = this.options;
    return typeof baseUrl === 'function' ? baseUrl() : baseUrl;
  }

  async signInWithPassword(): Promise<SignInWithPasswordResult> {
    //This is not supported in the current implementation
    throw new Error('Method Not Supported');
  }

  async signInWithMultiFactorAuth(_verificationCode: string): Promise<Tokens> {
    //This is not supported in the current implementation
    throw new Error('Method Not Supported');
  }

  async changePassword(): Promise<SignInWithPasswordSuccess> {
    //This is not supported in the current implementation
    throw new Error('Method Not Supported');
  }
  async forgotPassword(): Promise<Boolean> {
    //This is not supported in the current implementation
    throw new Error('Method Not Supported');
  }
  async verifyAndResetPassword(): Promise<Boolean> {
    //This is not supported in the current implementation
    throw new Error('Method Not Supported');
  }

  /**
   * Exchanges a Cognito id/access token for Apigee tokens.
   * The optional username is ignored; token is the one already issued by Cognito.
   */
  async signInWithCustomToken(
    token: string,
    _username: string,
  ): Promise<Tokens> {
    const response = await this.handleResponse(
      await fetch(
        `${this.baseUrl}/pg-token-implicit-aws/token?${new URLSearchParams({
          client_id: this.options.clientId,
          response_type: 'token',
          redirect_uri: '',
        })}`,
        {
          method: 'POST',
          headers: {
            idp_access_token: token,
          },
        },
      ),
    );
    const tokens = this.buildToken(response);
    return tokens;
  }

  async refreshTokens(idToken: string): Promise<Tokens> {
    const response = await this.handleResponse(
      await fetch(
        `${this.baseUrl}/pg-token-implicit-aws/token?${new URLSearchParams({
          client_id: this.options.clientId,
          response_type: 'token',
          redirect_uri: '',
        })}`,
        {
          method: 'POST',
          headers: {
            // Apigee endpoint expects this header name even when supplying the id token value
            idp_access_token: idToken,
          },
        },
      ),
    );
    const tokens = this.buildToken(response);
    return tokens;
  }

  protected buildToken(response: { [key: string]: any }): Tokens {
    return {
      idToken: null,
      refreshToken: null,
      accessToken: {
        token: response.access_token,
        payload: response,
      },
      expiresAt: parseInt(response.expires_at) * 1000, // convert from seconds to milliseconds
    };
  }
  protected async handleResponse(response: Response) {
    //TODO: Create Standard Errors
    if (!response.ok) {
      const error = new Error('Http error');
      try {
        const body = await response.json();
        // Apigee shapes errors as `{ fault: { string } }`; api-edge uses
        // `{ message }`. Unguarded `body.fault.string` threw a TypeError against
        // api-edge, which the catch below swallowed into a bare 'Http error' —
        // so every api-edge mint failure lost its real reason.
        const message = body?.fault?.string ?? body?.message;
        if (message) {
          error.message = message;
        }
      } catch (e) {
        throw error;
      }
      throw error;
    }
    return response.json();
  }
}

export default ApigeeAuthAdapter;
